// InteliChat #41 - alert ENGINE (framework-agnostic singleton).
//
// The SINGLE subscriber to the one real-time stream. actionCable.js emits
// `intelichat:new_conversation` / `intelichat:new_message` on the shared
// mitt bus (the same connector that feeds the Vuex store the HUD reads),
// so every loud channel here (sound, desktop notification, title cycle,
// favicon flash, PWA badge) is driven by the SAME event source as the HUD
// ground truth and can never drift out of sync. Never a separate poll.
//
// Mechanism only. All state/decisions (buckets, prefs, which conversation
// is open, current user, navigation) are injected by the Vue composable
// via a getContext() snapshot so there is exactly ONE decision owner and
// exactly ONE bus subscription (bootstrap is idempotent).
import { emitter } from 'shared/helpers/mitt';
import {
  primeOnGesture,
  playTone,
  startRepeat,
  stopRepeat,
  contextSuspended,
} from './sound';
import {
  showFaviconCount,
  startFaviconFlash,
  stopFaviconFlash,
  clearFavicon,
} from './faviconBadge';

const MESSAGE_TYPE_INCOMING = 0;
const FLASH_BURST_MS = 8000; // how long the favicon flashes after an arrival
const TITLE_CYCLE_MS = 1000;

class AlertEngine {
  constructor() {
    this.getContext = null; // () => snapshot, injected by the composable
    this.booted = false;
    this.primed = false;

    // title cycle
    this.baseTitle = document.title;
    this.titleTimer = null;
    this.titleOn = false;

    this.flashBurstTimer = null;

    this._onNewConversation = this._onNewConversation.bind(this);
    this._onNewMessage = this._onNewMessage.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onFirstGesture = this._onFirstGesture.bind(this);
  }

  // ---- bootstrap (idempotent) ---------------------------------------
  bootstrap(getContext) {
    this.getContext = getContext;
    if (this.booted) return;
    this.booted = true;

    emitter.on('intelichat:new_conversation', this._onNewConversation);
    emitter.on('intelichat:new_message', this._onNewMessage);
    document.addEventListener('visibilitychange', this._onVisibility);

    // autoplay-unlock primer on the agent's FIRST gesture this session
    ['click', 'keydown', 'touchstart'].forEach(ev =>
      window.addEventListener(ev, this._onFirstGesture, {
        once: false,
        passive: true,
      })
    );
  }

  _ctx() {
    try {
      return (this.getContext && this.getContext()) || {};
    } catch {
      return {};
    }
  }

  async _onFirstGesture() {
    if (this.primed) return;
    this.primed = await primeOnGesture();
    if (this.primed) {
      ['click', 'keydown', 'touchstart'].forEach(ev =>
        window.removeEventListener(ev, this._onFirstGesture)
      );
    }
  }

  // ---- notification permission (gesture-driven ONLY) ----------------
  // eslint-disable-next-line class-methods-use-this
  notificationPermission() {
    return 'Notification' in window ? Notification.permission : 'unsupported';
  }

  async requestNotificationPermission() {
    if (!('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }

  _canNotify() {
    const { prefs = {} } = this._ctx();
    return (
      'Notification' in window &&
      Notification.permission === 'granted' &&
      prefs.notificationsOn !== false
    );
  }

  // The CENTERPIECE: a foreground desktop Notification that survives the
  // agent being away (requireInteraction), re-alerts on a 2nd message
  // (tag=convId + renotify), and un-minimises to the RIGHT conversation
  // on click (window.focus() + navigate). When the browser is minimised
  // the OS draws the toast + gives its own taskbar/dock attention - that
  // is the "taskbar flash like tawk" behaviour, from the OS, for free.
  notify({ id, title, body }) {
    if (!this._canNotify() || !id) return null;
    try {
      const n = new Notification(title || 'New chat', {
        body: body || 'A customer is waiting',
        tag: `intelichat-conv-${id}`,
        renotify: true,
        requireInteraction: true,
        icon: '/favicon-32x32.png',
        badge: '/favicon-32x32.png',
      });
      n.onclick = event => {
        event.preventDefault();
        window.focus();
        const { navigate } = this._ctx();
        if (typeof navigate === 'function') navigate(id);
        n.close();
      };
      return n;
    } catch {
      return null;
    }
  }

  // ---- title cycling (Page-Visibility gated) ------------------------
  _startTitleCycle(count) {
    if (document.visibilityState === 'visible') return; // never cycle a focused tab
    if (this.titleTimer) return;
    this.baseTitle = document.title.replace(/^\(\d+\+?\)\s.*?•\s?/, '');
    const label = `(${count > 9 ? '9+' : count}) New chat`;
    this.titleTimer = setInterval(() => {
      this.titleOn = !this.titleOn;
      document.title = this.titleOn ? `${label} • ${this.baseTitle}` : this.baseTitle;
    }, TITLE_CYCLE_MS);
  }

  _stopTitleCycle() {
    if (this.titleTimer) {
      clearInterval(this.titleTimer);
      this.titleTimer = null;
    }
    if (this.baseTitle) document.title = this.baseTitle;
  }

  // ---- PWA badge (installed-app dock/taskbar unread; bonus channel) --
  // eslint-disable-next-line class-methods-use-this
  _setAppBadge(count) {
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) navigator.setAppBadge(count);
        else navigator.clearAppBadge();
      }
    } catch {
      /* not installed / unsupported - never the primary channel */
    }
  }

  // ---- counters: called by the composable whenever the reactive
  //      NEW bucket changes, so the passive channels track the ground
  //      truth exactly (title/favicon/pwa). ------------------------------
  setCounters(newCount = 0) {
    const n = Number(newCount) || 0;
    this._setAppBadge(n);
    if (n <= 0) {
      this._stopTitleCycle();
      clearFavicon();
      stopRepeat();
      return;
    }
    if (document.visibilityState === 'visible') {
      this._stopTitleCycle();
      showFaviconCount(n);
    } else {
      this._startTitleCycle(n);
      showFaviconCount(n);
    }
  }

  // brief attention burst on a fresh arrival (favicon flash), regardless
  // of tab visibility. The persistent TITLE CYCLE is intentionally NOT
  // started here: it is owned solely by setCounters(), which is driven by
  // the reactive act-now count and therefore self-clears to 0. Starting it
  // here let a bare inbound on a NON-act-now conversation (pending, already
  // answered, or an old/inactive thread not in the loaded list) leave a
  // "(1) New chat" title blinking forever - actNowCount never changed, so
  // setCounters(0) never fired to tear it down. The favicon flash below is
  // transient and self-clears via the burst timer.
  _pulse(newCount) {
    startFaviconFlash(newCount);
    if (this.flashBurstTimer) clearTimeout(this.flashBurstTimer);
    this.flashBurstTimer = setTimeout(() => {
      stopFaviconFlash();
      // settle back to the steady ground-truth count badge (or clear). Also
      // stop any stray title cycle when there is no live act-now item, so a
      // transient burst can never outlive the ground truth.
      const { newCount: live = 0 } = this._ctx();
      if (live > 0) {
        showFaviconCount(live);
      } else {
        clearFavicon();
        this._stopTitleCycle();
      }
    }, FLASH_BURST_MS);
  }

  _onVisibility() {
    if (document.visibilityState === 'visible') {
      // agent is back at the tab: quiet the passive attention channels
      this._stopTitleCycle();
      stopFaviconFlash();
      const { newCount = 0, openId } = this._ctx();
      if (newCount > 0) showFaviconCount(newCount);
      else clearFavicon();
      // if they're looking at the open conversation, stop the loop
      if (openId) stopRepeat();
    } else {
      const { newCount = 0 } = this._ctx();
      if (newCount > 0) this._startTitleCycle(newCount);
    }
  }

  // ---- the two bus handlers (the ONE stream) ------------------------
  _isSuppressed(conversationId) {
    // focus-suppression: the conversation the agent already has OPEN and
    // is looking at must not sound/flash/title/notify.
    const { openId } = this._ctx();
    return (
      openId &&
      Number(openId) === Number(conversationId) &&
      document.visibilityState === 'visible'
    );
  }

  _onNewConversation(payload) {
    const ctx = this._ctx();
    const id = payload?.id;
    if (!id) return;
    if (this._isSuppressed(id)) return;
    const conv = ctx.getConversation ? ctx.getConversation(id, payload) : null;
    const brief = conv || {
      id,
      name: payload?.meta?.sender?.name || 'Customer',
      lastLine: 'started a new chat',
      isNew: true,
    };
    this._alert({ brief, isNew: true, prefs: ctx.prefs, newCount: ctx.newCount });
  }

  _onNewMessage(payload) {
    const ctx = this._ctx();
    const type = payload?.message_type;
    const conversationId = payload?.conversation_id ?? payload?.conversation?.id;
    if (conversationId == null) return;
    // outgoing / activity / from the current agent -> not an inbound alert
    if (type !== undefined && type !== MESSAGE_TYPE_INCOMING) return;
    if (ctx.currentUserId && payload?.sender_id === ctx.currentUserId) return;

    if (this._isSuppressed(conversationId)) {
      stopRepeat(); // they're reading it - kill any loop
      return;
    }
    const conv = ctx.getConversation
      ? ctx.getConversation(conversationId, payload)
      : null;
    const brief = conv || {
      id: conversationId,
      name: payload?.sender?.name || 'Customer',
      lastLine: payload?.content || 'sent a message',
      isNew: false,
    };
    // NEW/unassigned incoming -> repeat-until-answered; a message in a
    // chat you already have -> single-shot.
    const isNew = brief.isNew || brief.isUnassigned;
    this._alert({ brief, isNew, prefs: ctx.prefs, newCount: ctx.newCount });
  }

  _alert({ brief, isNew, prefs = {}, newCount = 0 }) {
    // sound
    if (prefs.soundOn !== false) {
      if (isNew) {
        startRepeat({ tone: prefs.tone || 'ding', volume: prefs.volume ?? 0.8 });
      } else {
        playTone({ tone: prefs.tone || 'ding', volume: prefs.volume ?? 0.8 });
      }
    }
    // desktop notification (centerpiece)
    this.notify({
      id: brief.id,
      title: isNew ? `New chat from ${brief.name}` : `${brief.name} replied`,
      body: brief.lastLine,
    });
    // passive channels burst
    this._pulse(Math.max(newCount, 1));
  }

  // sound helpers the shell controls call directly
  // eslint-disable-next-line class-methods-use-this
  stopRepeat() {
    stopRepeat();
  }

  // eslint-disable-next-line class-methods-use-this
  soundSuspended() {
    return contextSuspended();
  }

  // eslint-disable-next-line class-methods-use-this
  async primeSound() {
    return primeOnGesture();
  }

  // eslint-disable-next-line class-methods-use-this
  testTone(prefs = {}) {
    return playTone({ tone: prefs.tone || 'ding', volume: prefs.volume ?? 0.8 });
  }
}

const engine = new AlertEngine();
export default engine;
